import React, { useEffect, useState } from "react";
import HeroSlider from "@/components/HeroSlider";
import FeaturedPosterCarousel from "@/components/FeaturedPosterCarousel";
import TourCard from "@/components/TourCard";
import { api, fileUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, Compass, ShieldCheck, Star, Phone, Wallet } from "lucide-react";

const BADGES = [
  { icon: ShieldCheck, title: "16 years of trust", sub: "Serving since 2010" },
  { icon: Compass, title: "Tour manager", sub: "On every departure" },
  { icon: Users, title: "Suitable for all ages", sub: "Senior-friendly design" },
  { icon: Wallet, title: "Transparent pricing", sub: "No hidden charges" },
];

export default function Home() {
  const [fixed, setFixed] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [homeStats, setHomeStats] = useState([]);
  const [homeIntro, setHomeIntro] = useState("");
  const [homeBandImage, setHomeBandImage] = useState("");
  const [visible, setVisible] = useState(3);
  const [vertical, setVertical] = useState(false);
  const [tIndex, setTIndex] = useState(0);
  const [anim, setAnim] = useState(true);

  useEffect(() => {
    api.get("/tours?category=Group%20Tours").then((r) => setFixed(r.data)).catch(()=>{});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(()=>{});
    api.get("/home-content").then((r) => { setHomeStats(r.data.stats || []); setHomeIntro(r.data.intro || ""); setHomeBandImage(r.data.bandImage || ""); }).catch(()=>{});
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => {
      setVisible(mq.matches ? 2 : 3);
      setVertical(mq.matches);
    };
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const canLoop = testimonials.length > visible;

  useEffect(() => {
    setTIndex(0);
    setAnim(true);
  }, [visible, testimonials.length]);

  useEffect(() => {
    if (!canLoop) return undefined;
    const id = setInterval(() => setTIndex((i) => i + 1), 5000);
    return () => clearInterval(id);
  }, [canLoop]);

  useEffect(() => {
    if (anim) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    return () => cancelAnimationFrame(raf);
  }, [anim]);

  const loopSlides = canLoop
    ? [...testimonials, ...testimonials.slice(0, visible)]
    : testimonials;

  const handleTrackTransitionEnd = () => {
    if (canLoop && tIndex >= testimonials.length) {
      setAnim(false);
      setTIndex((i) => i - testimonials.length);
    }
  };

  return (
    <>
      <HeroSlider />

      <section className="bg-white border-y border-amber-900/10" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {BADGES.map((b) => (
            <div key={b.title} className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-full bg-amber-100 grid place-items-center text-maroon shrink-0"><b.icon size={26} /></span>
              <div>
                <div className="font-display font-bold text-lg text-maroon leading-none">{b.title}</div>
                <div className="text-slate-600 text-sm">{b.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <FeaturedPosterCarousel />

      <section className="py-20 bg-ivory" data-testid="fixed-departures-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-amber-700 text-sm font-semibold tracking-widest uppercase mb-3">Group Tours from Kolkata</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-maroon leading-tight">Upcoming Tours</h2>
              <p className="text-slate-600 mt-3 text-lg max-w-2xl">Confirmed dates, escorted from Kolkata, cozy groups. Each tour explores its region in depth — the way it deserves to be seen.</p>
            </div>
            <Link to="/tours" className="btn-maroon h-12 px-6 rounded-full font-semibold inline-flex items-center justify-center" data-testid="home-view-all-tours-top">
              View All Tours
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fixed.map((t) => <TourCard key={t.id} tour={t} />)}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/tours" className="btn-gold h-12 px-7 rounded-full font-semibold inline-flex items-center justify-center" data-testid="home-view-all-tours-bottom">
              View All Tours
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-maroon text-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-amber-300 text-sm font-semibold tracking-widest uppercase mb-3">Why our guests keep returning</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">Kolkata's Trusted Travel Agency for Affordable International Group Tours</h2>
            <p className="mt-6 text-amber-50/90 text-lg leading-relaxed">{homeIntro}</p>
            <div className="mt-8 grid grid-cols-2 gap-6" data-testid="home-stats">
              {homeStats.map((s, i) => (
                <div key={i} data-testid={`home-stat-${i}`}>
                  <div className="font-display text-4xl font-bold text-amber-300">{s.value}</div>
                  <div className="text-amber-100/80 text-sm mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] ring-4 ring-amber-400/40">
            <img src={fileUrl(homeBandImage) || "https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?crop=entropy&cs=srgb&fm=jpg&q=85"} alt="International group tour vacation from Kolkata by Global Compass" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-20 bg-white border-y border-amber-900/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <div className="text-amber-700 text-sm font-semibold tracking-widest uppercase mb-3">Voices from our travellers</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-maroon">Trusted by 1000+ Bengali Families across Kolkata &amp; West Bengal</h2>
          </div>
          <div
            className="testimonial-viewport"
            data-testid="testimonials-slider"
            style={vertical ? { height: "620px" } : undefined}
          >
            <div
              className={`testimonial-track ${vertical ? "is-vertical" : ""}`}
              style={{
                transform: `translate${vertical ? "Y" : "X"}(-${tIndex * (100 / visible)}%)`,
                transition: anim ? "transform 700ms ease-out" : "none",
                "--slide-basis": `${100 / visible}%`,
              }}
              onTransitionEnd={handleTrackTransitionEnd}
            >
              {loopSlides.map((testimonial, index) => {
                const initials = (testimonial.clientName || "?")
                  .split(" ")
                  .map((word) => word[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase();
                return (
                <article
                  key={`${testimonial.id}-${index}`}
                  className="testimonial-slide"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="card-warm no-lift p-7 h-full flex flex-col">
                    <div className="flex text-amber-500 mb-3">
                      {Array.from({ length: testimonial.rating || 5 }).map((_, starIndex) => (
                        <Star key={starIndex} size={18} fill="currentColor" />
                      ))}
                    </div>
                    <p className="text-slate-700 text-[16px] leading-relaxed italic line-clamp-6">
                      "{testimonial.reviewText}"
                    </p>
                    <div className="mt-auto pt-5 flex items-center gap-4 border-t border-amber-900/10">
                      {testimonial.photoUrl ? (
                        <img
                          src={fileUrl(testimonial.photoUrl)}
                          alt={testimonial.clientName}
                          className="w-14 h-14 rounded-full object-cover ring-2 ring-amber-200 shrink-0"
                          data-testid={`testimonial-photo-${testimonial.id}`}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-maroon text-amber-100 grid place-items-center font-display font-bold text-lg shrink-0" data-testid={`testimonial-initials-${testimonial.id}`}>
                          {initials}
                        </div>
                      )}
                      <div>
                        <div className="font-display font-bold text-maroon">
                          {testimonial.clientName}
                        </div>
                        <div className="text-amber-700 text-sm font-semibold">
                          Travelled to {testimonial.destinationVisited}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
          </div>
          {canLoop && (
            <div className="flex justify-center gap-2 mt-8" data-testid="testimonial-dots">
              {testimonials.map((testimonial, idx) => (
                <button
                  key={testimonial.id}
                  onClick={() => { setAnim(true); setTIndex(idx); }}
                  aria-label={`Testimonial ${idx + 1}`}
                  className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                    ((tIndex % testimonials.length) + testimonials.length) % testimonials.length === idx
                      ? "w-8 bg-maroon"
                      : "w-2 bg-amber-300"
                  }`}
                  data-testid={`testimonial-dot-${idx}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-amber-50 via-white to-amber-100/60">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-amber-700 text-sm font-semibold tracking-widest uppercase mb-4">Ready to travel?</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-maroon leading-tight">Book International Group Tours &amp; Custom Holidays from Kolkata</h2>
          <p className="mt-5 text-slate-700 text-lg leading-relaxed">There's always something for everyone. Call us for enquiries.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/tours?category=Group%20Tours" className="btn-gold px-8 h-14 rounded-full font-semibold text-lg inline-flex items-center justify-center min-w-[200px]" data-testid="home-cta-explore">
              Explore Tours
            </Link>
            <a href="tel:+919874250100" className="btn-maroon px-8 h-14 rounded-full font-semibold text-lg inline-flex items-center justify-center gap-2 min-w-[160px]" data-testid="home-cta-call">
              <Phone size={18} /> Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
